import { Button } from '@/components/ui/button';
import { FormDescription, FormLabel } from '@/components/ui/form';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { AppImageField, AppInputField } from '@/form/FormUI';
import { CircleX, PlusCircleIcon } from 'lucide-react';
import type { FieldArrayPath, FieldValues, Path, UseFormReturn } from 'react-hook-form';
import { useFieldArray } from 'react-hook-form';

type SelectOptionsEditorProps<T extends FieldValues> = {
    form: UseFormReturn<T>;
    name: FieldArrayPath<T>;
    storagePathPrefix: string;
};

export const SelectOptionsEditor = <T extends FieldValues>({
    form,
    name,
    storagePathPrefix,
}: SelectOptionsEditorProps<T>) => {
    const options = useFieldArray({ control: form.control, name });

    return (
        <div className="flex flex-col gap-2 group/options">
            <FormLabel>Options</FormLabel>
            <FormDescription>Values the user can choose from in the dropdown.</FormDescription>
            {options.fields.map((optionField, optionIndex) => {
                const titlePath = `${name}.${optionIndex}.title` as Path<T>;
                const subtitlePath = `${name}.${optionIndex}.subtitle` as Path<T>;
                const valuePath = `${name}.${optionIndex}.value` as Path<T>;
                const bgImagePath = `${name}.${optionIndex}.bgImage` as Path<T>;
                const title = form.getValues(titlePath) as string | undefined;
                return (
                    <Popover key={optionField.id}>
                        <PopoverTrigger asChild>
                            <div className="cursor-pointer border border-gray-300 rounded-lg px-3 py-2 bg-white hover:bg-gray-100 transition">
                                <span className="text-sm text-gray-700">{title || 'Untitled Option'}</span>
                            </div>
                        </PopoverTrigger>
                        <PopoverContent
                            side="left"
                            align="start"
                            className="w-[300px] p-4 bg-white shadow-md border rounded-md"
                        >
                            <div className="flex flex-col gap-3">
                                <AppInputField
                                    control={form.control}
                                    name={titlePath}
                                    label="Title"
                                    placeholder="Option Title"
                                    className="gap-1"
                                    smallLabel
                                />
                                <AppInputField
                                    control={form.control}
                                    name={subtitlePath}
                                    label="Subtitle (Optional)"
                                    placeholder="Examples or explanation..."
                                    className="gap-1"
                                    smallLabel
                                />
                                <AppInputField
                                    control={form.control}
                                    name={valuePath}
                                    label="Value"
                                    placeholder="option_value"
                                    className="gap-1"
                                    smallLabel
                                />
                                <AppImageField
                                    form={form}
                                    name={bgImagePath}
                                    label="Background Image"
                                    smallLabel
                                    storagePathPrefix={storagePathPrefix}
                                />
                                <div className="flex justify-end">
                                    <Button
                                        type="button"
                                        variant="destructive"
                                        onClick={() => options.remove(optionIndex)}
                                        className="cursor-pointer"
                                    >
                                        <CircleX /> Delete Option
                                    </Button>
                                </div>
                            </div>
                        </PopoverContent>
                    </Popover>
                );
            })}
            <div className="text-center">
                <Button
                    type="button"
                    onClick={() => options.append({ value: '', title: '' } as never)}
                    variant="ghost"
                    size="sm"
                    className="cursor-pointer h-6 text-xs"
                >
                    <PlusCircleIcon /> Add Option
                </Button>
            </div>
        </div>
    );
};
